// src/components/HRComponents/CaseCreateDrawer.js
import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import SelectDropdown from "../../customStyles/SelectDropdown";

export default function CaseCreateDrawer({ isOpen, onClose, categories, onSuccess }) {
  const [submitting, setSubmitting] = useState(false); // API சப்மிஷன் லோடிங் நிலை
  const [newCase, setNewCase] = useState({
    title: "",
    categoryId: "",
    description: "",
    priority: "medium",
    dueDate: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      const url = SummaryApi.createCase?.url || "/api/cases/create";
      const res = await api.post(url, newCase);
      if (res.data.success) {
        toast.success("New case has been raised successfully.");
        setNewCase({ title: "", categoryId: "", description: "", priority: "medium", dueDate: "" });
        onSuccess();
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to raise case.");
    } finally {
      setSubmitting(false); // லோடிங் நிலையை நிறுத்தவும்
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* லோடிங் ஆகும்போது பின்னணி பகுதியைக் கிளிக் செய்து மூடுவதைத் தடுக்கிறது */}
      <div 
        onClick={submitting ? null : onClose} 
        className="fixed inset-0 top-[57px] md:top-[65px] z-[65] bg-black/40 backdrop-blur-xs" 
      />
      <div className="fixed inset-y-0 top-[57px] md:top-[65px] right-0 z-[65] w-full max-w-md bg-white border-l shadow-2xl flex flex-col transition-transform duration-300 transform translate-x-0">
        
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-slate-50 shrink-0">
          <div className="text-left">
            <h3 className="font-bold text-slate-900 text-sm">Raise a New Case</h3>
            <p className="text-[11px] text-slate-400">File a support ticket or corporate incident</p>
          </div>
          <button 
            type="button"
            onClick={onClose} 
            disabled={submitting}
            className="p-1 hover:bg-slate-200 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 flex-1 overflow-y-auto space-y-4 text-left text-xs">
          
          <div className="space-y-1">
            <label className="font-bold text-slate-500 block">Case Subject *</label>
            <input 
              type="text" 
              required 
              disabled={submitting}
              value={newCase.title} 
              onChange={e => setNewCase({ ...newCase, title: e.target.value })} 
              placeholder="Subject details..." 
              className="w-full border border-slate-200 rounded-lg p-2 h-9 outline-none focus:border-red-400 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed" 
            />
          </div>

          <div className="space-y-1">
            <label className="font-bold text-slate-500 block">Category *</label>
            <SelectDropdown 
              options={categories} 
              value={newCase.categoryId} 
              disabled={submitting}
              onChange={val => setNewCase({ ...newCase, categoryId: val })} 
              placeholder="Select Category" 
              valueKey="value" 
              labelKey="label" 
              ChildClassName="h-9 border-slate-200" 
              searchable={true} 
              zIndex={66} 
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="font-bold text-slate-500 block">Priority Level</label>
              <SelectDropdown 
                options={[
                  { label: "Low", value: "low" }, 
                  { label: "Medium", value: "medium" }, 
                  { label: "High", value: "high" }, 
                  { label: "Urgent", value: "urgent" }
                ]} 
                value={newCase.priority} 
                disabled={submitting}
                onChange={val => setNewCase({ ...newCase, priority: val })} 
                placeholder="Choose Priority" 
                valueKey="value" 
                labelKey="label" 
                ChildClassName="h-9 border-slate-200" 
                zIndex={66} 
              />
            </div>

            <div className="space-y-1">
              <label className="font-bold text-slate-500 block">Target Due Date</label>
              <input 
                type="date" 
                disabled={submitting}
                value={newCase.dueDate} 
                onChange={e => setNewCase({ ...newCase, dueDate: e.target.value })} 
                className="w-full border border-slate-200 rounded-lg p-2 h-9 outline-none focus:border-red-400 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed" 
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="font-bold text-slate-500 block">Full Description *</label>
            <textarea 
              rows={6} 
              required 
              disabled={submitting}
              value={newCase.description} 
              onChange={e => setNewCase({ ...newCase, description: e.target.value })} 
              placeholder="Explain the incident details..." 
              className="w-full border border-slate-200 rounded-lg p-2.5 outline-none focus:border-red-400 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed resize-none" 
            />
          </div>

          {/* Action Buttons */}
          <div className="pt-4 flex gap-2">
            <button 
              type="submit" 
              disabled={submitting}
              className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg shadow transition-colors disabled:bg-red-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 h-9"
            >
              {submitting ? (
                <>
                  <span className="w-4.5 h-4.5 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
                  <span>Submitting...</span>
                </>
              ) : (
                <span>Submit Case</span>
              )}
            </button>
            <button 
              type="button" 
              onClick={onClose} 
              disabled={submitting}
              className="px-4 py-2 border rounded-lg text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all h-9"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  );
}